import { useEffect, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Target, Plus, Trash2, CheckCircle2, Clock, AlertCircle, Loader2, Users2, Eye, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pendiente: { label: "Pendiente", cls: "bg-slate-100 text-slate-700", icon: Clock },
  en_curso: { label: "En curso", cls: "bg-blue-100 text-blue-700", icon: Clock },
  hecho: { label: "Hecho", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  cancelado: { label: "Cancelado", cls: "bg-red-100 text-red-700", icon: AlertCircle },
};

export default function FocusDayPage() {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDlg, setOpenDlg] = useState(false);
  const [digestHtml, setDigestHtml] = useState(null);
  const [digestOwner, setDigestOwner] = useState("");
  const [form, setForm] = useState({
    title: "", description: "", owner_email: "", project_id: "",
    due_date: "", status: "pendiente", focus_day_date: "", priority: "medium",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [list, sum, projs] = await Promise.all([
        fetch(`${API}/focus-day/commitments`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API}/focus-day/summary`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API}/pg07/projects`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      ]);
      setItems(Array.isArray(list) ? list : []);
      setSummary(sum);
      setProjects(Array.isArray(projs) ? projs : []);
    } catch (e) {
      toast.error("Error cargando compromisos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!form.title || !form.owner_email || !form.due_date) {
      toast.error("Título, owner y fecha son obligatorios");
      return;
    }
    const proj = projects.find(p => p.id === form.project_id);
    const payload = {
      ...form,
      project_id: form.project_id || null,
      project_name: proj?.name || null,
    };
    const r = await fetch(`${API}/focus-day/commitments`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    if (r.ok) {
      toast.success("Compromiso creado. Recibirás recordatorios T-7 y T-1.");
      setOpenDlg(false);
      setForm({ title: "", description: "", owner_email: "", project_id: "", due_date: "", status: "pendiente", focus_day_date: "", priority: "medium" });
      load();
    } else {
      toast.error("Error al crear compromiso");
    }
  };

  const updateStatus = async (item, newStatus) => {
    const r = await fetch(`${API}/focus-day/commitments/${item.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...item, status: newStatus }),
    });
    if (r.ok) { toast.success("Estado actualizado"); load(); }
    else toast.error("Error");
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este compromiso?")) return;
    const r = await fetch(`${API}/focus-day/commitments/${id}`, {
      method: "DELETE", headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) { toast.success("Eliminado"); load(); }
    else toast.error("Error");
  };

  const openDigestPreview = async () => {
    const owner = digestOwner.trim() ||
      (JSON.parse(localStorage.getItem("user") || "{}")).email || "";
    const url = owner
      ? `${API}/focus-day/digest/preview?owner_email=${encodeURIComponent(owner)}`
      : `${API}/focus-day/digest/preview`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) { const j = await r.json(); setDigestHtml(j); }
    else toast.error("Error cargando preview");
  };

  const sendTestDigest = async () => {
    const r = await fetch(`${API}/focus-day/digest/send-test`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) {
      const j = await r.json();
      toast.success(`Email enviado a ${j.to} (${j.items_count} compromisos)`);
    } else {
      const err = await r.json().catch(() => ({}));
      toast.error(err.detail || "Error enviando email de prueba");
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = (d, s) => d < today && ["pendiente", "en_curso"].includes(s);

  return (
    <div className="space-y-6" data-testid="focus-day-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Focus Day</div>
          <h1 className="text-3xl font-black flex items-center gap-2"><Target className="w-7 h-7 text-emerald-600" /> Compromisos & Seguimiento</h1>
          <p className="text-sm text-muted-foreground mt-1">Compromisos generados en el Focus Day mensual. Recordatorios automáticos por email T-7 y T-1 al owner.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={openDigestPreview} data-testid="digest-preview-btn">
            <Eye className="w-4 h-4 mr-1" />Vista previa digest
          </Button>
          <Dialog open={openDlg} onOpenChange={setOpenDlg}>
          <DialogTrigger asChild>
            <Button data-testid="focus-day-new-btn"><Plus className="w-4 h-4 mr-1" />Nuevo compromiso</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="focus-day-form-dialog">
            <DialogHeader><DialogTitle>Nuevo compromiso Focus Day</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Título *</Label><Input data-testid="fd-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ej: Cerrar propuesta X" /></div>
              <div><Label>Descripción</Label><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Owner email *</Label><Input data-testid="fd-owner" type="email" value={form.owner_email} onChange={e => setForm({ ...form, owner_email: e.target.value })} placeholder="usuario@valorizasm.com" /></div>
                <div><Label>Fecha límite *</Label><Input data-testid="fd-due" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Proyecto (opcional)</Label>
                  <Select value={form.project_id} onValueChange={v => setForm({ ...form, project_id: v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {projects.slice(0, 100).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Prioridad</Label>
                  <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Baja</SelectItem>
                      <SelectItem value="medium">Media</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Fecha del Focus Day (opcional)</Label><Input type="date" value={form.focus_day_date} onChange={e => setForm({ ...form, focus_day_date: e.target.value })} /></div>
              <Button onClick={save} className="w-full" data-testid="fd-save-btn"><Plus className="w-4 h-4 mr-1" />Crear compromiso</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>

        <Dialog open={!!digestHtml} onOpenChange={(o) => !o && setDigestHtml(null)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto" data-testid="digest-preview-dialog">
            <DialogHeader>
              <DialogTitle>Preview digest semanal</DialogTitle>
            </DialogHeader>
            {digestHtml && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                  <span>Owner:</span>
                  <Input value={digestOwner} onChange={e => setDigestOwner(e.target.value)} placeholder={digestHtml.owner} className="h-7 max-w-xs" data-testid="digest-owner-input" />
                  <Button size="sm" variant="outline" onClick={openDigestPreview} data-testid="digest-refresh">Recargar</Button>
                  <Button size="sm" onClick={sendTestDigest} data-testid="digest-send-test">
                    <Send className="w-3 h-3 mr-1" />Enviar prueba a mí
                  </Button>
                </div>
                <div className="text-xs">
                  <div className="mb-1"><span className="text-muted-foreground">Asunto:</span> <b>{digestHtml.subject}</b></div>
                  <div className="text-muted-foreground">Vencidos: {digestHtml.overdue_count} · Próximos: {digestHtml.upcoming_count}</div>
                </div>
                <div className="border rounded p-2 bg-white dark:bg-slate-950" data-testid="digest-preview-html">
                  <div dangerouslySetInnerHTML={{ __html: digestHtml.html }} />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="flex justify-center p-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div> : (
        <>
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="focus-day-summary">
              <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Total</div><div className="text-3xl font-black">{summary.total}</div></CardContent></Card>
              <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Pendientes</div><div className="text-3xl font-black text-slate-700">{summary.by_status.pendiente}</div></CardContent></Card>
              <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Hechos</div><div className="text-3xl font-black text-emerald-700">{summary.by_status.hecho}</div></CardContent></Card>
              <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Vencidos</div><div className="text-3xl font-black text-red-700">{summary.overdue}</div></CardContent></Card>
            </div>
          )}

          <Card data-testid="focus-day-list">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">Compromiso</th>
                    <th className="text-left px-3 py-2">Owner</th>
                    <th className="text-left px-3 py-2">Proyecto</th>
                    <th className="text-left px-3 py-2">Vence</th>
                    <th className="text-left px-3 py-2">Estado</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c, i) => {
                    const meta = STATUS_META[c.status] || STATUS_META.pendiente;
                    const overdue = isOverdue(c.due_date, c.status);
                    return (
                      <tr key={c.id} className="border-t hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`fd-row-${i}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium">{c.title}</div>
                          {c.description && <div className="text-xs text-muted-foreground truncate max-w-md">{c.description}</div>}
                        </td>
                        <td className="px-3 py-3 text-xs">{c.owner_email}</td>
                        <td className="px-3 py-3 text-xs text-muted-foreground">{c.project_name || "—"}</td>
                        <td className="px-3 py-3 text-xs">
                          <span className={overdue ? "text-red-700 font-semibold" : ""}>{c.due_date}</span>
                          {overdue && <div className="text-[10px] text-red-600">VENCIDO</div>}
                        </td>
                        <td className="px-3 py-3">
                          <Select value={c.status} onValueChange={v => updateStatus(c, v)}>
                            <SelectTrigger className="w-32 h-8 text-xs" data-testid={`fd-status-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-3">
                          <Button variant="ghost" size="icon" onClick={() => remove(c.id)} data-testid={`fd-delete-${i}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </td>
                      </tr>
                    );
                  })}
                  {!items.length && (<tr><td colSpan={6} className="text-center py-10 text-muted-foreground">Sin compromisos aún. Crea uno con el botón &quot;Nuevo compromiso&quot;.</td></tr>)}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {summary?.ranking_by_owner?.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-bold flex items-center gap-2 mb-3"><Users2 className="w-4 h-4" /> Ranking por owner (% completado)</h3>
                <div className="space-y-2">
                  {summary.ranking_by_owner.slice(0, 10).map((o, i) => (
                    <div key={o.owner} className="flex items-center gap-3 text-sm" data-testid={`fd-ranking-${i}`}>
                      <span className="w-6 text-muted-foreground">{i + 1}</span>
                      <span className="flex-1 truncate">{o.owner}</span>
                      <Badge variant="outline" className="text-xs">{o.total} total</Badge>
                      {o.overdue > 0 && <Badge className="text-xs bg-red-100 text-red-700">{o.overdue} vencidos</Badge>}
                      <span className="font-mono font-semibold w-12 text-right">{o.pct_done}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
